import { useCallback, useEffect, useState } from "react";
import { authDownload } from "../lib/auth";
import { toast } from "sonner";
import { Boxes, Download, FileText, ShieldCheck } from "lucide-react";
import { api, API, shortDate } from "../lib/api";
import { NeonButton } from "./common";

const Verify = ({ v }) => (
  <div className={`mt-2 p-2 border font-mono text-[10px] ${v.valid ? "border-brand/30 text-brand" : "border-[#F87171]/40 text-[#F87171]"}`} data-testid="package-verify-result">
    {v.valid ? "PACKAGE INTACT" : "PACKAGE INVALID"} · root {v.merkle_root_valid ? "ok" : "mismatch"} · TSA {v.tsa_valid ? "verified" : "unverified"} · {v.leaves_checked} leaves
    {v.issues.length > 0 && <div className="text-[#F87171]">{v.issues.map((i) => `${i.ref}: ${i.issue}`).join(" · ")}</div>}
    {v.transaction_changed_since_seal.length > 0 && <div className="text-[#FFB800]">changed since seal: {v.transaction_changed_since_seal.join(", ")} (snapshot preserved)</div>}
  </div>
);

const Pkg = ({ p }) => {
  const [v, setV] = useState(null);
  return (
    <li className="py-3 border-b border-divider" data-testid={`package-${p.id}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="font-mono text-[11px] text-white">{p.id} <span className="text-white/35">· {p.leaf_count} leaves · {shortDate(p.created_at)}</span></p>
          <p className="font-mono text-[10px] text-white/40 truncate">merkle root {p.merkle_root}</p>
          <p className={`font-mono text-[10px] ${p.tsa?.status === "granted" ? "text-accent2" : "text-[#F87171]"}`}>
            RFC 3161 · {p.tsa?.status === "granted" ? `${p.tsa.gen_time} · serial ${p.tsa.serial}` : p.tsa?.error || "not granted"}
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <button onClick={() => api.get(`/packages/${p.id}/verify`).then((r) => setV(r.data))} className="font-mono text-[10px] uppercase text-white/50 hover:text-brand" data-testid={`verify-package-${p.id}`}>verify</button>
          <a href="#pdf" onClick={(e) => { e.preventDefault(); authDownload(`${API}/packages/${p.id}/pdf`, `evidence-${p.id}.pdf`); }} className="text-white/50 hover:text-brand" data-testid={`pdf-package-${p.id}`} title="Issuer-ready PDF"><FileText className="w-3.5 h-3.5" /></a>
          <a href="#download" onClick={(e) => { e.preventDefault(); authDownload(`${API}/packages/${p.id}/download`); }} className="text-white/50 hover:text-brand" data-testid={`download-package-${p.id}`}><Download className="w-3.5 h-3.5" /></a>
        </div>
      </div>
      {v && <Verify v={v} />}
    </li>
  );
};

export const EvidencePackage = ({ disputeId }) => {
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get(`/disputes/${disputeId}/packages`).then((r) => setList(r.data)), [disputeId]);
  useEffect(() => { load(); }, [load]);
  const seal = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/disputes/${disputeId}/package`);
      toast.success(`Sealed · root ${data.merkle_root.slice(0, 12)} · TSA ${data.tsa.status}`);
      load();
    } finally { setBusy(false); }
  };
  return (
    <div data-testid="evidence-package-panel">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs text-white/50 flex items-center gap-2"><ShieldCheck className="w-3.5 h-3.5 text-brand" /> Merkle tree over every signed record + consent + transaction snapshot</p>
        <NeonButton onClick={seal} disabled={busy} className="!py-1.5" data-testid="seal-package-btn"><Boxes className="w-3 h-3" /> {busy ? "Sealing…" : "Seal package"}</NeonButton>
      </div>
      <ul>{list.map((p) => <Pkg key={p.id} p={p} />)}</ul>
      {list.length === 0 && <p className="font-mono text-[11px] text-white/35">No sealed package yet.</p>}
    </div>
  );
};
